import { ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import { PowerButton } from '@vy/ui-design-system';
import { CONTRACT_STATUSES } from '@vy/types';
import type { FC } from 'react';
import { useCallback } from 'react';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useUpdateContractStatus } from '../../../../../hooks/requests/use-update-contract-status';
import { useDialog } from '../../../../../hooks/use-dialog';
import { runMutation } from '../../../../../utils/vy-mutation';

type ActivateContractMenuItemProps = {
  isActive: boolean;
  contractId: string;
  disabled?: boolean;
};

export const ActivateContractMenuItem: FC<ActivateContractMenuItemProps> = ({
  isActive,
  contractId,
  disabled,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const updateContractStatusMutation = useUpdateContractStatus(contractId);

  const actionLabel = isActive ? 'Deactivate' : 'Activate';
  const dialogContent = isActive
    ? 'You’re about to deactivate the contract. This may pause all activity. Are you sure?'
    : "You're about to activate the contract. This action can't be undone. Are you sure?";

  const handleConfirm = useCallback(async () => {
    handleClose();
    const status = isActive ? CONTRACT_STATUSES.Inactive : CONTRACT_STATUSES.Active;
    await runMutation(
      updateContractStatusMutation,
      { status },
      {
        pending: `Updating contract status to ${status}...`,
        success: `Contract status updated to ${status}`,
        error: `Failed to update contract status to ${status}`,
      },
    );
  }, [handleClose, isActive, updateContractStatusMutation]);

  return (
    <>
      <MenuItem disabled={disabled} onClick={handleOpen}>
        <ListItemIcon>
          <PowerButton />
        </ListItemIcon>
        <ListItemText>{actionLabel}</ListItemText>
      </MenuItem>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`${actionLabel} Contract`}
        disableDividers
        ctaText={actionLabel}
        onSubmit={handleConfirm}
        cancelButtonText="Cancel"
        dialogContentProps={{
          style: { padding: '24px 48px', minWidth: '400px' },
        }}
      >
        <div
          style={{
            inlineSize: '400px',
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
            overflowWrap: 'break-word',
          }}
        >
          <Typography align="center">{dialogContent}</Typography>
        </div>
      </VyDialog>
    </>
  );
};
